// @ts-nocheck
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Calendar, DollarSign, Target, TrendingUp, Play, Pause, Eye, Plus } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import CampaignForm from "./CampaignForm";
import { parseLocalDate } from '@/utils/formatters';

interface Campaign {
  id: string;
  campaign_name: string;
  campaign_type: string;
  start_date: string;
  end_date: string;
  total_budget: number;
  ad_placements: string[];
  target_impressions: number;
  actual_impressions: number;
  click_through_rate: number;
  campaign_status: string;
  performance_notes: string;
  advertiser: {
    company_name: string;
    advertiser_tier: string;
  };
}

const CampaignManager: React.FC = () => {
  const { profile } = useAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCampaignForm, setShowCampaignForm] = useState(false);

  useEffect(() => {
    if (profile?.id) {
      fetchCampaigns();
    }
  }, [profile?.id]);

  const fetchCampaigns = async () => {
    try {
      const { data, error } = await supabase
        .from('advertising_campaigns')
        .select(`
          *,
          advertiser:advertiser_prospects!inner(
            company_name,
            advertiser_tier
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCampaigns(data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planned': return 'bg-gray-100 text-gray-800';
      case 'live': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'local': return 'bg-blue-100 text-blue-800';
      case 'regional': return 'bg-purple-100 text-purple-800';
      case 'national': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const calculateProgress = (actual: number, target: number) => {
    return target > 0 ? Math.min((actual / target) * 100, 100) : 0;
  };

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const handleToggleCampaign = async (campaignId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'live' ? 'paused' : 'live';
    
    try {
      const { error } = await supabase
        .from('advertising_campaigns')
        .update({ campaign_status: newStatus })
        .eq('id', campaignId);

      if (error) throw error;

      setCampaigns(prev => 
        prev.map(campaign => 
          campaign.id === campaignId 
            ? { ...campaign, campaign_status: newStatus }
            : campaign
        )
      );
    } catch (error) {
      console.error('Error updating campaign status:', error);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="h-6 bg-muted rounded"></div>
                <div className="h-4 bg-muted rounded"></div>
                <div className="h-20 bg-muted rounded"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Active Campaigns</h3>
          <p className="text-sm text-muted-foreground">Manage your advertising campaigns</p>
        </div>
        <Button onClick={() => setShowCampaignForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Campaign
        </Button>
      </div>

      {campaigns.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">No campaigns found. Create your first campaign to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {campaigns.map((campaign) => {
            const impressionProgress = calculateProgress(
              campaign.actual_impressions, 
              campaign.target_impressions
            );
            const daysRemaining = getDaysRemaining(campaign.end_date);

            return (
              <Card key={campaign.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{campaign.campaign_name}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {campaign.advertiser.company_name}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Badge className={getTierColor(campaign.advertiser.advertiser_tier)}>
                        {campaign.advertiser.advertiser_tier}
                      </Badge>
                      <Badge className={getStatusColor(campaign.campaign_status)}>
                        {campaign.campaign_status}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Campaign Details */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span>{parseLocalDate(campaign.start_date).toLocaleDateString()} - {parseLocalDate(campaign.end_date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span>{formatCurrency(campaign.total_budget)}</span>
                    </div>
                  </div>

                  {/* Performance Metrics */}
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium">Impressions Progress</span>
                        <span className="text-sm text-muted-foreground">
                          {formatNumber(campaign.actual_impressions)} / {formatNumber(campaign.target_impressions)}
                        </span>
                      </div>
                      <Progress value={impressionProgress} className="h-2" />
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">CTR:</span>
                        <span className="ml-2 font-medium">{(campaign.click_through_rate * 100).toFixed(2)}%</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Days Remaining:</span>
                        <span className="ml-2 font-medium">{daysRemaining}</span>
                      </div>
                    </div>
                  </div>

                  {/* Ad Placements */}
                  <div>
                    <span className="text-sm font-medium">Placements:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {campaign.ad_placements.map((placement, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {placement}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Performance Notes */}
                  {campaign.performance_notes && (
                    <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                      {campaign.performance_notes}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleCampaign(campaign.id, campaign.campaign_status)}
                      className="gap-1"
                    >
                      {campaign.campaign_status === 'live' ? (
                        <>
                          <Pause className="h-3 w-3" />
                          Pause
                        </>
                      ) : (
                        <>
                          <Play className="h-3 w-3" />
                          Start
                        </>
                      )}
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1">
                      <Eye className="h-3 w-3" />
                      Details
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1">
                      <TrendingUp className="h-3 w-3" />
                      Analytics
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {showCampaignForm && (
        <CampaignForm
          open={showCampaignForm}
          onClose={() => setShowCampaignForm(false)}
          onSuccess={() => {
            setShowCampaignForm(false);
            fetchCampaigns();
          }}
        />
      )}
    </div>
  );
};

export default CampaignManager;